import DOMPurify from 'dompurify';
import parse from 'html-react-parser';
import { MouseEvent, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { bridgeAtom, isFontsLoadedAtom, useValue } from '@/store';
import { Background, CloseButton, Content, Crest, Star, Stars, Title, Tooltip, Wrapper } from './Default.styles';

interface DefaultWidgetProps {
  closeBtn: boolean;
  isTooltipOpen: boolean;
  tooltipText: string;
  toggleHover: (state: boolean) => void;
  onTooltipHided: () => void;
  closeTooltip: (e: MouseEvent) => void;
  onClick: () => void;
}

export const DefaultLayout = ({
  isTooltipOpen,
  tooltipText,
  onTooltipHided,
  toggleHover,
  closeBtn,
  closeTooltip,
  onClick,
}: DefaultWidgetProps) => {
  const [isReady, setIsReady] = useState(false);
  const [width, setWidth] = useState<number>(60);

  const ignoreHoverRef = useRef<boolean>(false);

  const isFontsLoaded = useValue(isFontsLoadedAtom);
  const { translations, handleIframeMouseEvents } = useValue(bridgeAtom);

  const titleRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const titleWidthRef = useRef<number>(0);
  const tooltipWidthRef = useRef<number>(0);
  const isFirstRenderRef = useRef(true);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const widgetTitle = translations.widget?.title ?? '';

  const onAnimationEnd = useCallback(
    (event: React.TransitionEvent) => {
      const currentOpacity = Number(window.getComputedStyle(event.target as HTMLElement).opacity);
      if (currentOpacity !== 0) {
        return;
      }

      if (isFirstRenderRef.current) {
        isFirstRenderRef.current = false;
      }
      onTooltipHided();
    },
    [onTooltipHided]
  );

  const onMouseEnter = useCallback(() => {
    toggleHover(true);
  }, [toggleHover]);

  const onMouseLeave = useCallback(() => {
    ignoreHoverRef.current = false;
    toggleHover(false);
  }, [toggleHover]);

  useLayoutEffect(() => {
    if (!tooltipRef.current || !titleRef.current) {
      return;
    }

    titleWidthRef.current = titleRef.current.getBoundingClientRect().width;
    tooltipWidthRef.current = tooltipRef.current.getBoundingClientRect().width;
    setIsReady(true);
  }, [isFontsLoaded]);

  useEffect(() => {
    if (!tooltipRef.current) {
      return;
    }

    tooltipWidthRef.current = tooltipRef.current.getBoundingClientRect().width;
  }, [tooltipText]);

  useEffect(() => {
    if (!isReady) {
      return;
    }

    setWidth(titleWidthRef.current);
  }, [isReady]);

  useEffect(() => {
    setWidth(isTooltipOpen ? tooltipWidthRef.current : titleWidthRef.current);
  }, [isTooltipOpen, closeBtn]);

  useEffect(() => {
    if (!handleIframeMouseEvents) {
      return;
    }

    const handleMouseMove = (e: any) => {
      if (!wrapperRef.current) {
        return;
      }

      const rect = wrapperRef.current?.getBoundingClientRect();
      const isOver =
        e.clientX >= rect.left && e.clientX <= rect.right && e.clientY >= rect.top && e.clientY <= rect.bottom;

      const iframeEl = document.getElementById('background-site');
      if (isOver) {
        onMouseEnter();
        if (iframeEl) {
          iframeEl.style.pointerEvents = 'none';
        }
      } else {
        onMouseLeave();
        if (iframeEl) {
          iframeEl.style.pointerEvents = 'auto';
        }
      }
    };

    document.addEventListener('mousemove', handleMouseMove);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
    };
  }, [onMouseEnter, onMouseLeave, handleIframeMouseEvents]);

  if (!isFontsLoaded) {
    return null;
  }

  return (
    <Wrapper
      ref={wrapperRef}
      onClick={onClick}
      onMouseEnter={handleIframeMouseEvents ? undefined : onMouseEnter}
      onMouseLeave={handleIframeMouseEvents ? undefined : onMouseLeave}
      data-testid="saywhatt-widget-wrapper"
      id="saywhatt-widget-wrapper"
    >
      <Content width={width} isReady={isReady}>
        <Title ref={titleRef} isVisible={!isTooltipOpen} isTitleSet={Boolean(widgetTitle)}>
          <Stars>
            <Star />
            <Star />
          </Stars>

          <p data-testid="saywhatt-widget-title">
            {parse(DOMPurify.sanitize(widgetTitle, { USE_PROFILES: { html: true } }))}
          </p>
        </Title>

        <Tooltip ref={tooltipRef} isVisible={isTooltipOpen} onTransitionEnd={onAnimationEnd}>
          {closeBtn && (
            <CloseButton onClick={closeTooltip} data-testid="saywhatt-widget-popup-close">
              <Crest />
            </CloseButton>
          )}
          <p data-testid="saywhatt-widget-popup-text">
            {parse(DOMPurify.sanitize(tooltipText, { USE_PROFILES: { html: true } }))}
          </p>
        </Tooltip>
      </Content>

      <Background isTooltipOpen={isTooltipOpen} isAnimate={false} />
    </Wrapper>
  );
};
